import { useEffect, useMemo, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { InteractionStatus } from '@azure/msal-browser'
import type { AccountInfo } from '@azure/msal-browser'
import { loginRequest } from '../msalConfig'
import { getPreferredTheme, storeThemePreference, themes } from '../theme'
import type { ThemeMode, ThemeTokens } from '../theme'
import peekMark from '../assets/peek-mark.svg'
import peekMascot from '../assets/chu-peek.png'
import { PeoplePage } from '../pages/People'
import { BudgetsPage } from '../pages/Budgets'
import { KeysPage } from '../pages/Keys'
import { AccessPage } from '../pages/Access'
import { ModelsPage } from '../pages/Models'
import { OnboardingPage } from '../pages/Onboarding'
import { OverviewPage } from '../pages/Overview'
import { DashboardsPage } from '../pages/Dashboards'
import { ArcadePortal, PortalSignInScreen } from '../pages/StaffPortal'
import { SettingsPage } from '../pages/Settings'
import { AuditLogPage } from '../pages/AuditLog'
import { BudgetRequestsPage } from '../pages/BudgetRequests'
import { ReportsPage } from '../pages/Reports'
import { LeaderboardPage } from '../pages/Leaderboard'
import { GuidesPage } from '../pages/Guides'

interface NavItem {
  label: string
  path: string
  /** Shown under the heading on that page's placeholder; describes the API surface it will drive (Tasks 8-9). */
  description: string
}

/**
 * Nine nav items become eight.
 *
 * `Access` (a read-only report) and `Access grants` (an editor) listed the
 * same ten servers twice and forced the operator to hold "what is true" and
 * "how to change it" on two different screens. They are now one page whose
 * cells are both the fact and the control.
 */
const NAV_ITEMS: NavItem[] = [
  { label: 'Overview', path: '/overview', description: 'Platform status: ECS services, alarms, and the access report at a glance.' },
  { label: 'Access', path: '/access', description: 'MCP server access (live) and agent shares (cached) — two planes, never one grid.' },
  { label: 'Models', path: '/models', description: 'Which models each person can use: assign groups and people, see who is affected before saving, and which models send identifiers offshore unmasked.' },
  { label: 'People', path: '/people', description: 'One place for a person: access, capability groups, offboarding, their developer key, and both ceilings (chat and key).' },
  { label: 'Onboarding', path: '/onboarding', description: 'Select people, grant MCP access, and send the welcome email. Phase 1: grants and email, no developer keys yet.' },
  { label: 'Keys', path: '/keys', description: 'Developer virtual keys: issue, block, and retire.' },
  { label: 'Budget requests', path: '/budget-requests', description: 'Self-service requests for a higher developer-key budget: approve, reject, and their full timeline.' },
  { label: 'Reports', path: '/reports', description: 'Scheduled sends of the Underwriting Team View: who gets it, how often, and the outcome of every send.' },
  { label: 'Budgets', path: '/budgets', description: 'Platform-wide spend: chat/developer/platform-key summary, and platform key ceilings. Per-person detail lives on People.' },
  { label: 'Dashboards', path: '/dashboards', description: 'Cost, usage, and adoption, read from the analytics warehouse.' },
  { label: 'Leaderboard', path: '/board', description: 'The full staff arcade, mounted inside the admin console: same board, admin chrome.' },
  { label: 'Guides', path: '/guides', description: 'The same staff guides as the portal: how to use CHU-GPT, Claude Desktop, Claude Code, the MCP tools, dashboards, and prompts.' },
  { label: 'Audit log', path: '/audit', description: 'Every operator-attributed admin-panel change, filterable by operator, resource, and date.' },
  { label: 'Settings', path: '/settings', description: 'Leaderboard settings: excluded accounts and hide-costs.' },
]

/** Reads the "Admin" app role the same way the backend does: a `roles` array on the token claims. */
function readRoles(idTokenClaims: AccountInfo['idTokenClaims']): string[] {
  const roles = (idTokenClaims as { roles?: unknown } | undefined)?.roles
  return Array.isArray(roles) ? roles.filter((role): role is string => typeof role === 'string') : []
}

/** The token's `exp` claim, in ms, or null when it carries none. */
function readExpiry(idTokenClaims: AccountInfo['idTokenClaims']): number | null {
  const exp = (idTokenClaims as { exp?: unknown } | undefined)?.exp
  return typeof exp === 'number' ? exp * 1000 : null
}

/** Up to two initials from a display name, falling back to the UPN. */
export function initialsFor(name: string | undefined, upn: string | undefined): string {
  const source = (name ?? upn ?? '?').trim()
  const words = source.split(/[\s.@_-]+/).filter(Boolean)
  if (words.length === 0) {
    return '?'
  }
  const first = words[0]?.[0] ?? '?'
  const second = words.length > 1 ? (words[1]?.[0] ?? '') : ''
  return (first + second).toUpperCase()
}

/**
 * A stable hue for a person, derived from their UPN.
 *
 * The same technique the arcade's `PixelAvatar` already uses: Graph returns
 * 404 for anyone with no photo, so the fallback has to be deterministic
 * rather than random, or an operator's own avatar changes colour on every
 * page load.
 */
export function hueFor(upn: string | undefined): number {
  const source = upn ?? ''
  let hash = 0
  for (let i = 0; i < source.length; i += 1) {
    hash = (hash * 31 + source.charCodeAt(i)) % 360
  }
  return hash
}

function useThemeMode(): [ThemeMode, ThemeTokens, (mode: ThemeMode) => void] {
  const [mode, setMode] = useState<ThemeMode>(() => getPreferredTheme())

  useEffect(() => {
    storeThemePreference(mode)
    // Native form controls (checkboxes, selects, scrollbars) follow the
    // document's color-scheme, not the tokens we paint with. index.html
    // declares `light dark`, so an operator whose OS is in dark mode but
    // whose console is light got dark-scheme checkboxes: solid black squares
    // on a cream page (found live 23 Sep on the People filter row). Pin the
    // scheme to the mode actually being painted.
    document.documentElement.style.colorScheme = mode
    // index.html pre-paints body from prefers-color-scheme so there is no
    // white flash before React mounts; once the mode is known the body must
    // match it too, or overscroll on a light console shows the dark pre-paint
    // as a black frame (seen on the Budgets page, 23 Sep).
    document.body.style.background = themes[mode].background
  }, [mode])

  // The profile menu sets a mode explicitly rather than toggling, because it
  // renders both options and shows which one is current.
  return [mode, themes[mode], setMode]
}

function buildStyles(tokens: ThemeTokens): Record<string, CSSProperties> {
  return {
    fullScreen: {
      minHeight: '100vh',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: tokens.background,
      color: tokens.textPrimary,
      fontFamily: 'inherit',
      padding: '2rem',
    },
    card: {
      width: '100%',
      maxWidth: '26rem',
      background: tokens.surface,
      borderRadius: '1.25rem',
      boxShadow: tokens.shadow,
      padding: '2.4rem 2.2rem',
      textAlign: 'center',
    },
    mascot: {
      width: '5.75rem',
      height: '5.75rem',
      objectFit: 'contain',
      margin: '0 auto 1.2rem',
      display: 'block',
    },
    heading: {
      fontFamily: 'Georgia, "Times New Roman", serif',
      fontWeight: 500,
      fontSize: '1.6rem',
      margin: '0 0 0.5rem',
      color: tokens.textPrimary,
    },
    subheading: {
      fontSize: '0.94rem',
      color: tokens.textSecondary,
      margin: '0 0 1.6rem',
      lineHeight: 1.5,
    },
    primaryButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      width: '100%',
      padding: '0.8rem 1.4rem',
      borderRadius: '9999px',
      border: 'none',
      background: tokens.brand,
      color: tokens.brandContrast,
      fontWeight: 600,
      fontSize: '0.95rem',
      cursor: 'pointer',
    },
    ghostButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      padding: '0.7rem 1.2rem',
      borderRadius: '9999px',
      border: `1.5px solid ${tokens.border}`,
      background: 'transparent',
      color: tokens.textPrimary,
      fontWeight: 600,
      fontSize: '0.9rem',
      cursor: 'pointer',
      marginTop: '0.75rem',
    },
    shell: {
      display: 'flex',
      minHeight: '100vh',
      width: '100%',
      background: tokens.background,
      color: tokens.textPrimary,
    },
    nav: {
      width: '15.5rem',
      flexShrink: 0,
      background: tokens.navBackground,
      color: tokens.navText,
      display: 'flex',
      flexDirection: 'column',
      padding: '1.6rem 1.1rem',
    },
    brandRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.65rem',
      padding: '0 0.3rem 1.6rem',
      color: tokens.navTextActive,
    },
    markIcon: {
      width: '1.9rem',
      height: '1.9rem',
      flexShrink: 0,
    },
    brandTitle: {
      fontWeight: 700,
      fontSize: '1.02rem',
      lineHeight: 1.1,
    },
    brandSubtitle: {
      fontSize: '0.78rem',
      color: tokens.navText,
    },
    navList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.15rem',
      flex: 1,
    },
    navLink: {
      display: 'block',
      padding: '0.6rem 0.75rem',
      borderRadius: '0.6rem',
      color: tokens.navText,
      textDecoration: 'none',
      fontSize: '0.92rem',
      fontWeight: 500,
    },
    navFooter: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.4rem',
      paddingTop: '1rem',
      borderTop: `1px solid rgba(255, 255, 255, 0.14)`,
    },
    navFooterButton: {
      display: 'block',
      width: '100%',
      textAlign: 'left',
      padding: '0.55rem 0.75rem',
      borderRadius: '0.6rem',
      border: 'none',
      background: 'transparent',
      color: tokens.navText,
      fontSize: '0.86rem',
      cursor: 'pointer',
    },
    main: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '1.2rem 2rem',
      borderBottom: `1px solid ${tokens.border}`,
      background: tokens.surface,
    },
    headerTitle: {
      fontSize: '1.15rem',
      fontWeight: 700,
      color: tokens.textPrimary,
    },
    operator: {
      fontSize: '0.85rem',
      color: tokens.textSecondary,
    },
    profileWrap: {
      position: 'relative',
    },
    profileTrigger: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.55rem',
      padding: '0.3rem 0.7rem 0.3rem 0.35rem',
      borderRadius: '9999px',
      border: `1px solid ${tokens.border}`,
      background: 'transparent',
      color: tokens.textPrimary,
      fontSize: '0.85rem',
      fontWeight: 600,
      cursor: 'pointer',
      fontFamily: 'inherit',
    },
    avatar: {
      width: '1.9rem',
      height: '1.9rem',
      borderRadius: '9999px',
      objectFit: 'cover',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.72rem',
      fontWeight: 700,
      color: '#FFFFFF',
      flexShrink: 0,
    },
    profileMenu: {
      position: 'absolute',
      top: 'calc(100% + 0.4rem)',
      right: 0,
      minWidth: '17rem',
      zIndex: 40,
      background: tokens.surface,
      border: `1px solid ${tokens.border}`,
      borderRadius: '0.8rem',
      boxShadow: tokens.shadow,
      overflow: 'hidden',
    },
    profileSection: {
      padding: '0.85rem 1rem',
      borderBottom: `1px solid ${tokens.border}`,
    },
    profileMenuItem: {
      display: 'block',
      width: '100%',
      textAlign: 'left',
      padding: '0.6rem 1rem',
      border: 'none',
      background: 'transparent',
      color: tokens.textPrimary,
      fontSize: '0.86rem',
      fontFamily: 'inherit',
      cursor: 'pointer',
    },
    segmented: {
      display: 'inline-flex',
      borderRadius: '0.5rem',
      border: `1px solid ${tokens.border}`,
      overflow: 'hidden',
    },
    content: {
      flex: 1,
      padding: '2rem',
      overflow: 'auto',
    },
    placeholderCard: {
      background: tokens.surface,
      border: `1px solid ${tokens.border}`,
      borderRadius: '1rem',
      padding: '1.8rem',
      maxWidth: '38rem',
    },
    placeholderTitle: {
      margin: '0 0 0.5rem',
      fontSize: '1.05rem',
      color: tokens.textPrimary,
    },
    placeholderBody: {
      margin: 0,
      fontSize: '0.92rem',
      color: tokens.textSecondary,
      lineHeight: 1.55,
    },
  }
}

function FullScreenMessage({
  tokens,
  title,
  subtitle,
  children,
}: {
  tokens: ThemeTokens
  title: string
  subtitle: string
  children?: ReactNode
}) {
  const styles = buildStyles(tokens)
  return (
    <div style={styles.fullScreen}>
      <div style={styles.card}>
        <img src={peekMascot} alt="" style={styles.mascot} />
        <h1 style={styles.heading}>{title}</h1>
        <p style={styles.subheading}>{subtitle}</p>
        {children}
      </div>
    </div>
  )
}

function SignInScreen({ tokens, onSignIn }: { tokens: ThemeTokens; onSignIn: () => void }) {
  const styles = buildStyles(tokens)
  return (
    <FullScreenMessage
      tokens={tokens}
      title="Welcome to CHU-GPT Admin"
      subtitle="Platform administration for CHU-GPT. Sign in with your CHU Microsoft account."
    >
      <button type="button" style={styles.primaryButton} onClick={onSignIn}>
        Sign in with Microsoft
      </button>
    </FullScreenMessage>
  )
}

function AccessDeniedScreen({
  tokens,
  account,
  onSignOut,
}: {
  tokens: ThemeTokens
  account: AccountInfo | undefined
  onSignOut: () => void
}) {
  const styles = buildStyles(tokens)
  const signedInAs = account?.username ?? account?.name ?? 'your CHU account'
  return (
    <FullScreenMessage
      tokens={tokens}
      title="Access denied"
      subtitle={`${signedInAs} is signed in but does not hold the Admin role in CHU-GPT. Ask the CTO to assign it in Entra, or sign out and switch accounts.`}
    >
      <button type="button" style={styles.ghostButton} onClick={onSignOut}>
        Sign out
      </button>
    </FullScreenMessage>
  )
}

/** Every page in `NAV_ITEMS` is live: Budgets/Keys/Access from Task 8,
 *  Overview/Dashboards from Task 9, Audit log from the unified-admin-access-control build's Task 2.
 *  Users (onboard/offboard, capability groups, the Admin role) is GONE as of
 *  Unit 16: onboarding moved to `/onboarding`, the Admin role to `/settings`
 *  back on an earlier round, and offboarding plus capability-group editing
 *  fold into the People person panel in this one -- see People.tsx's own
 *  "Unit 16" comments. `/users` now redirects to `/people` (below) rather
 *  than 404ing every bookmark and every `?person=` deep link this file's
 *  other pages already send there. */
const PAGE_COMPONENTS: Partial<Record<string, (props: { tokens: ThemeTokens }) => ReactNode>> = {
  '/overview': OverviewPage,
  '/budgets': BudgetsPage,
  '/people': PeoplePage,
  '/onboarding': OnboardingPage,
  '/keys': KeysPage,
  '/budget-requests': BudgetRequestsPage,
  '/reports': ReportsPage,
  '/dashboards': DashboardsPage,
  '/access': AccessPage,
  '/models': ModelsPage,
  '/audit': AuditLogPage,
  '/settings': SettingsPage,
}

/**
 * The operator's avatar: their Graph photo, or deterministic initials.
 *
 * The photo comes from `/api/me/photo`, which proxies Graph `/me/photo/$value`
 * with the operator's own delegated token. Graph 404s for anyone with no
 * photo — ordinary, not an error — so the route answers 204 and this falls
 * back to initials on a hue derived from the UPN. The blob is held as an
 * object URL for the session and revoked on unmount; it is never persisted.
 *
 * Exported (staff-portal restyle, 23 Sep 2026): `StaffPortal.tsx` reuses this
 * exact component for the arcade host's header, rather than a second photo
 * fetch -- the admin console and the staff portal show the same photo the
 * same way, fetched once per component instance.
 */
export function OperatorAvatar({
  account,
  style,
}: {
  account: AccountInfo | undefined
  // `buildStyles` is a Record, so an index read is possibly-undefined under
  // this tsconfig's noUncheckedIndexedAccess.
  style: CSSProperties | undefined
}) {
  const [photoUrl, setPhotoUrl] = useState<string | null>(null)

  useEffect(() => {
    let revoked = false
    let objectUrl: string | null = null
    void (async () => {
      try {
        const { api } = await import('../api')
        const response = await api('/api/me/photo')
        if (response.status !== 200) {
          return
        }
        const blob = await response.blob()
        if (blob.size === 0 || revoked) {
          return
        }
        objectUrl = URL.createObjectURL(blob)
        setPhotoUrl(objectUrl)
      } catch {
        // No photo, or Graph unreachable. Initials are a correct answer,
        // not a degraded one, so this is silent by design.
      }
    })()
    return () => {
      revoked = true
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [account?.homeAccountId])

  if (photoUrl) {
    return <img src={photoUrl} alt="" style={style} />
  }
  return (
    <span
      style={{
        ...style,
        background: `hsl(${hueFor(account?.username)} 45% 38%)`,
        color: '#FFFFFF',
      }}
      aria-hidden="true"
    >
      {initialsFor(account?.name, account?.username)}
    </span>
  )
}

/**
 * The profile menu (spec 4.6): avatar, identity, roles, session expiry,
 * appearance and sign out.
 *
 * Sign out and the theme toggle move here out of the nav footer, which is an
 * unusual place for either. "Session expires in NN min" is worth its small
 * cost: MSAL knows the expiry, and an operator who understands why a call
 * suddenly needs a step-up stops filing it as a bug.
 */
function ProfileMenu({
  tokens,
  mode,
  setMode,
  account,
  roles,
  onSignOut,
}: {
  tokens: ThemeTokens
  mode: ThemeMode
  setMode: (mode: ThemeMode) => void
  account: AccountInfo | undefined
  roles: string[]
  onSignOut: () => void
}) {
  const styles = buildStyles(tokens)
  const [open, setOpen] = useState(false)
  const [now, setNow] = useState(() => Date.now())

  // Tick once a minute while the menu is open, so the countdown is not a
  // number frozen at the moment the page happened to load.
  useEffect(() => {
    if (!open) {
      return
    }
    const timer = setInterval(() => setNow(Date.now()), 60_000)
    return () => clearInterval(timer)
  }, [open])

  const expiry = readExpiry(account?.idTokenClaims)
  const minutesLeft = expiry !== null ? Math.round((expiry - now) / 60_000) : null

  return (
    <div style={styles.profileWrap}>
      <button
        type="button"
        style={styles.profileTrigger}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        onClick={() => setOpen((prev) => !prev)}
      >
        <OperatorAvatar account={account} style={styles.avatar} />
        <span>{account?.name ?? account?.username ?? 'Account'}</span>
        <span aria-hidden="true" style={{ color: tokens.textSecondary }}>
          ▾
        </span>
      </button>
      {open ? (
        <>
          {/* Click-away. A bare overlay rather than a document listener so
              it cannot outlive the menu. */}
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 30 }}
            role="presentation"
            onClick={() => setOpen(false)}
          />
          <div style={styles.profileMenu} role="menu">
            <div style={styles.profileSection}>
              <div style={{ display: 'flex', gap: '0.7rem', alignItems: 'center' }}>
                <OperatorAvatar
                  account={account}
                  style={{ ...styles.avatar, width: '2.6rem', height: '2.6rem', fontSize: '0.9rem' }}
                />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: tokens.textPrimary }}>
                    {account?.name ?? account?.username ?? 'Signed in'}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: tokens.textSecondary, wordBreak: 'break-all' }}>
                    {account?.username ?? ''}
                  </div>
                </div>
              </div>
              <div style={{ fontSize: '0.8rem', color: tokens.textSecondary, marginTop: '0.55rem' }}>
                Roles: {roles.length > 0 ? roles.join(', ') : 'none'}
              </div>
              <div style={{ fontSize: '0.8rem', color: tokens.textSecondary }}>
                {minutesLeft === null
                  ? 'Session expiry unknown'
                  : minutesLeft > 0
                    ? `Session expires in ${minutesLeft} min`
                    : 'Session expired — the next action will re-authenticate'}
              </div>
            </div>

            <div style={{ ...styles.profileSection, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.6rem' }}>
              <span style={{ fontSize: '0.85rem', color: tokens.textPrimary }}>Appearance</span>
              <span style={styles.segmented}>
                {(['light', 'dark'] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setMode(option)}
                    aria-pressed={mode === option}
                    style={{
                      padding: '0.35rem 0.7rem',
                      border: 'none',
                      background: mode === option ? tokens.brand : 'transparent',
                      color: mode === option ? tokens.brandContrast : tokens.textSecondary,
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    {option === 'light' ? 'Light' : 'Dark'}
                  </button>
                ))}
              </span>
            </div>

            <button
              type="button"
              role="menuitem"
              style={styles.profileMenuItem}
              onClick={() => {
                setOpen(false)
                onSignOut()
              }}
            >
              Sign out
            </button>
          </div>
        </>
      ) : null}
    </div>
  )
}

function PlaceholderPage({ tokens, item }: { tokens: ThemeTokens; item: NavItem }) {
  const styles = buildStyles(tokens)
  return (
    <div style={styles.placeholderCard}>
      <h2 style={styles.placeholderTitle}>{item.label}</h2>
      <p style={styles.placeholderBody}>{item.description}</p>
    </div>
  )
}

function AdminShell({
  tokens,
  mode,
  setMode,
  account,
  roles,
  onSignOut,
}: {
  tokens: ThemeTokens
  mode: ThemeMode
  setMode: (mode: ThemeMode) => void
  account: AccountInfo | undefined
  roles: string[]
  onSignOut: () => void
}) {
  const styles = buildStyles(tokens)
  const location = useLocation()
  const activeItem = NAV_ITEMS.find((item) => location.pathname.startsWith(item.path)) ?? NAV_ITEMS[0]

  return (
    <div style={styles.shell}>
      <aside style={styles.nav}>
        <div style={styles.brandRow}>
          <img src={peekMark} alt="" style={styles.markIcon} />
          <div>
            <div style={styles.brandTitle}>CHU-GPT</div>
            <div style={styles.brandSubtitle}>Admin</div>
          </div>
        </div>
        <nav style={styles.navList} aria-label="CHU-GPT Admin">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              style={({ isActive }) => ({
                ...styles.navLink,
                background: isActive ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                color: isActive ? tokens.navTextActive : tokens.navText,
              })}
            >
              {item.label}
            </NavLink>
          ))}
          <NavLink
            to="/arcade"
            style={({ isActive }) => ({
              ...styles.navLink,
              marginTop: '0.4rem',
              background: isActive ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
              color: isActive ? tokens.navTextActive : tokens.navText,
            })}
          >
            🕹 Arcade
          </NavLink>
        </nav>
        {/* Sign out and the theme toggle moved into the profile menu, which
            is where an operator looks for them. The footer keeps only the
            build's own identity. */}
        <div style={styles.navFooter}>
          <span style={{ ...styles.navFooterButton, cursor: 'default', fontSize: '0.75rem', opacity: 0.7 }}>
            CHU-GPT Admin
          </span>
        </div>
      </aside>
      <div style={styles.main}>
        <header style={styles.header}>
          <span style={styles.headerTitle}>{activeItem?.label ?? 'Overview'}</span>
          <ProfileMenu
            tokens={tokens}
            mode={mode}
            setMode={setMode}
            account={account}
            roles={roles}
            onSignOut={onSignOut}
          />
        </header>
        <main style={styles.content}>
          <Routes>
            <Route path="/" element={<Navigate to="/overview" replace />} />
            {/* Unit 16: Users is gone; anyone with the old nav item bookmarked
                or a stale link lands on People instead of a 404. */}
            <Route path="/users" element={<Navigate to="/people" replace />} />
            {NAV_ITEMS.map((item) => {
              // The Leaderboard section takes isAdmin/onSignOut, not the
              // {tokens} shape every other PAGE_COMPONENTS entry gets, so it
              // is special-cased here rather than forcing that map's props
              // to a union every OTHER page would have to account for.
              // `isAdmin` is always true here already: reaching AdminShell
              // at all required the caller's own `!isAdmin` gate to have
              // passed (see AppShell's isArcade/isAdmin ordering comment,
              // unchanged by this addition).
              if (item.path === '/board') {
                return <Route key={item.path} path={item.path} element={<LeaderboardPage chrome="embedded" isAdmin onSignOut={onSignOut} />} />
              }
              // `/guides/*`, not `/guides`: a guide slug follows (`/guides/
              // claude-desktop`) and react-router only matches that under a
              // trailing wildcard. GuidesPage reads the slug itself off
              // `window.location.pathname` (see its own doc comment for why
              // it does not take a route param), so no `useParams` here.
              if (item.path === '/guides') {
                return <Route key={item.path} path="/guides/*" element={<GuidesPage chrome="standalone" />} />
              }
              const PageComponent = PAGE_COMPONENTS[item.path]
              return (
                <Route
                  key={item.path}
                  path={item.path}
                  element={PageComponent ? <PageComponent tokens={tokens} /> : <PlaceholderPage tokens={tokens} item={item} />}
                />
              )
            })}
            <Route path="*" element={<Navigate to="/overview" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  )
}

/**
 * Root shell for the CHU-GPT Admin SPA.
 *
 * Renders one of three states, in order:
 * 1. Unauthenticated -> a CHU-branded sign-in screen.
 * 2. Authenticated but missing the "Admin" app role -> access-denied. The
 *    backend is the real gate (every `/api/*` route requires the Admin role
 *    on the bearer token, see `backend/src/auth/verifyToken.ts`); this is
 *    purely a friendlier reflection of that same check so a non-admin never
 *    sees a bare 403 flash before an API call fails.
 * 3. Authenticated Admin -> the full shell: nav rail (Overview / Access /
 *    People / Onboarding / Keys / Budgets / Dashboards / Audit log / Settings)
 *    and a placeholder for whichever page is routed to; the pages themselves
 *    land in Tasks 8-9.
 */
export function AppShell() {
  const [mode, tokens, setMode] = useThemeMode()
  const isAuthenticated = useIsAuthenticated()
  const { instance, accounts, inProgress } = useMsal()
  const location = useLocation()

  const activeAccount = instance.getActiveAccount() ?? accounts[0]
  const roles = useMemo(() => readRoles(activeAccount?.idTokenClaims), [activeAccount])
  const isAdmin = roles.includes('Admin')
  // The arcade shows on the /arcade path OR on the dedicated staff host
  // (arcade.chugpt...), where it is the whole point of the site.
  const isArcade =
    location.pathname.startsWith('/arcade') || window.location.hostname.startsWith('arcade.')

  const handleSignIn = () => {
    instance.loginRedirect(loginRequest).catch((error: unknown) => {
      console.error('CHU-GPT Admin sign-in failed', error)
    })
  }

  const handleSignOut = () => {
    instance.logoutRedirect({ postLogoutRedirectUri: '/' }).catch((error: unknown) => {
      console.error('CHU-GPT Admin sign-out failed', error)
    })
  }

  if (inProgress !== InteractionStatus.None) {
    return (
      <FullScreenMessage
        tokens={tokens}
        title="Signing in"
        subtitle="Talking to CHU's Microsoft Entra tenant..."
      />
    )
  }

  if (!isAuthenticated || !activeAccount) {
    // The arcade host gets its own, arcade-themed sign-in landing (staff
    // portal restyle, 23 Sep 2026) rather than the admin console's cream
    // screen: a staff member arriving from the onboarding email should land
    // on something that already looks like the board they are about to
    // use. `isArcade` is a plain path/hostname check computed above, so
    // this branch decision carries no security weight of its own -- the
    // ordering that matters (isArcade before isAdmin) is below, unchanged.
    return isArcade ? <PortalSignInScreen onSignIn={handleSignIn} /> : <SignInScreen tokens={tokens} onSignIn={handleSignIn} />
  }

  // The arcade host (staff portal: Home, Leaderboard, Developer key) is open
  // to ANY signed-in CHU person, not just admins, so it is resolved BEFORE
  // the Admin-role gate. Every route it reaches (`/api/leaderboard`,
  // `/api/me/*`) is behind `requireUser` at most, so a non-admin's token
  // works there and nowhere else in the panel. THIS ORDERING IS THE ENTIRE
  // MECHANISM that keeps the admin nav (`NAV_ITEMS`/`AdminShell` below) off
  // this host: it must never move after the `isAdmin` check.
  // `ArcadePortal` is the policy-acceptance gate (Round 14, Unit 10) wrapped
  // around `StaffPortalShell` -- ONE insertion point ahead of every tab this
  // host offers, so Home/Leaderboard/Developer key/Guides all sit behind it
  // without a per-tab check to forget. `StaffPortalShell` itself renders the
  // existing `LeaderboardPage` unchanged for its own tab; see
  // `pages/StaffPortal.tsx` for why Home/Developer key are not folded into
  // that component's own `Mode` system instead.
  if (isArcade) {
    return <ArcadePortal account={activeAccount} isAdmin={isAdmin} onSignOut={handleSignOut} />
  }

  if (!isAdmin) {
    return <AccessDeniedScreen tokens={tokens} account={activeAccount} onSignOut={handleSignOut} />
  }

  return (
    <AdminShell
      tokens={tokens}
      mode={mode}
      setMode={setMode}
      account={activeAccount}
      roles={roles}
      onSignOut={handleSignOut}
    />
  )
}

export default AppShell
